// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SystemSettingFetcher.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import fetch from 'share/fetch/Izenda.Fetch';
import { get, isNil } from 'lodash/fp';
import CacheUtils from 'share/utils/Izenda.CacheUtils';
const ROOT_URL = 'systemSetting/';

export default {
  BASE_PATH: ROOT_URL,

  /**
   * Fetch System Settings
   */
  loadSystemSettings() {
    return fetch(`${ROOT_URL}`).get();
  },

  /**
   * Fetch system mode from API
   * @returns {Promise}
   */
  getSystemMode() {
    return fetch(`${ROOT_URL}systemMode`)
      .get()
      .then(get('systemMode'));
  },

  /**
   * Fetch Cookie Options
   * @returns {Promise}
   */
  getCookieOptions() {
    return fetch(`${ROOT_URL}cookieOptions`).get();
  },

  /**
   * Fetch system mode from API
   * @returns {Promise}
   */
  getSystemVariables() {
    return fetch(`${ROOT_URL}systemVariables`).get();
  },

  /**
   * Fetch system indicator from API
   * @param {string} tenantId
   * @returns {Promise}
   */
  getSystemIndicator(tenantId: string) {
    const tenant = isNil(tenantId) ? '' : tenantId;
    return fetch(`${ROOT_URL}systemIndicator/${tenant}`).get();
  },
  /**
   * fetch number of connection to disable context menu link
   * @param  {String} tenantId
   */
  getNumberOfConnection(tenantId: string) {
    const tenant = isNil(tenantId) ? '' : tenantId;
    return fetch(`connection/visible/${tenant}/count`).get();
  },

  /**
   * Fetch list active tenant
   * @returns {Promise}
   */
  getTenants() {
    return fetch('tenant/activeTenants').get();
  },

  /**
   * [getAllUsers Fetch list users]
   * @param {string} tenantId
   * @return {Array}
   */
  getAllUsers(tenantId: string) {
    tenantId = tenantId || '';
    return fetch(`user/all/${tenantId}`).get();
  },

  /**
   * [getPartialUsers Fetch list users]
   * @param {Object} params
   * @return {Array}
   */
  getPartialUsers(params: any) {
    return fetch('user/all/').post(params);
  },

  /**
   * Load Schedules
   * @param {object} data
   * @returns {Promise}
   */
  loadSchedules(data: any) {
    return fetch(`${ROOT_URL}loadSchedules`).post(data);
  },

  /**
   * Delete Schedules, data is array of schedule ID
   * @param {array} data
   * @returns {Promise}
   */
  deleteSchedules(data: Array<any>) {
    return fetch(`${ROOT_URL}deleteSchedules`).post(data);
  },

  /**
   * Get Report Dashboard Name
   * @param {string}
   * @param tenantId
   * @returns {promise}
   */
  getReportDashboardNameByTenant(reportingType: string, tenantId: string) {
    const param = tenantId || '';
    return fetch(`${ROOT_URL}schedules/reportDashboardName/${reportingType}/${param}`).get();
  },

  /**
   * Get Recipients by Tenant
   * @param {string} tenantId
   * @returns {promise}
   */
  getRecipientByTenant(tenantId: string) {
    const param = tenantId || '';
    return fetch(`${ROOT_URL}schedules/recipients/${param}`).get();
  },

  /**
   * get Creator by Tenant
   * @param {string} tenantId
   * @returns {promise}
   */
  getCreatedByTenant(tenantId: string) {
    const param = tenantId || '';
    return fetch(`${ROOT_URL}schedules/creators/${param}`).get();
  },

  /**
   * Load email variables
   * @param  {Number} reportingType
   * @param  {Object} pageInfo
   */
  loadEmailVariables(reportingType: number, pageInfo: any) {
    pageInfo = pageInfo || {};
    const defaultPageIndex = 1;
    const defaultPageSize = 10;

    const requestData = {
      reportingType,
      criteria: pageInfo.searchCriteria || [],
      pageIndex: pageInfo.pageIndex || defaultPageIndex,
      pageSize: pageInfo.pageSize || defaultPageSize,
      sortOrders: pageInfo.sortOrders || [
        {
          key: 'name',
          descending: false
        }
      ]
    };

    return fetch(`${ROOT_URL}loadEmailVariables`).post(requestData);
  },

  /**
   * load Security Policies
   * @return {pormise}
   */
  loadSecurityPolicies() {
    return fetch(`${ROOT_URL}securityPolicy`).get();
  },

  /**
   * save Security Policies
   * @param {Object} any
   * @return {promise}
   */
  saveSecurityPolicies(data: any) {
    data = data || {};
    return fetch(`${ROOT_URL}securityPolicy`).post(data);
  },

  /**
   * Load security questions.
   * @param {string} userName
   * @param {string} tenantDisplayID
   */
  securityQuestions({ userName, tenantDisplayID }: { userName: string, tenantDisplayID: string }) {
    const request = {
      userName,
      tenantDisplayID
    };
    return fetch(`${ROOT_URL}securityQuestions`).post(request);
  },

  /**
   * Check database setup.
   */
  databaseSetup() {
    return fetch(`${ROOT_URL}databaseSetup`).get();
  },

  /**
   * Load Email Configuration
   * @param {string} tenantId
   * @returns {promise}
   */
  loadEmailConfiguration(tenantId) {
    tenantId = tenantId || '';
    return fetch(`${ROOT_URL}email/${tenantId}`).get();
  },

  /**
   * Save Email Configuration
   * @param {Object} data
   */
  saveEmailConfiguration(data: any) {
    return fetch(`${ROOT_URL}email`).post(data);
  },

  /**
   * Check whether Email is configured or not
   * @param {string} tenantId
   * @returns {promise}
   */
  isHavemailConfiguration(tenantId: string) {
    tenantId = tenantId || '';
    return fetch(`${ROOT_URL}email/status/${tenantId}`).get();
  },

  /**
   * load All Languages
   * @return {promise}
   */
  loadAllLanguages() {
    return fetch(`${ROOT_URL}language`).get();
  },

  /**
   * load All Date Format
   * @return {promise}
   */
  loadAllDateFormat() {
    return fetch(`${ROOT_URL}dateFormat`).get();
  },
  /**
   * load System Setting Mode
   * @return {promise}
   */
  loadSystemModeSettings() {
    return fetch(`${ROOT_URL}systemModeSettings`).get();
  },

  /**
   * save System Setting Mode
   * @param {Object} data
   * @return {promise}
   */
  saveSystemModeSettings(data: any) {
    data = data || {};
    return fetch(`${ROOT_URL}systemModeSettings`).post(data);
  },

  /**
   * Fetch deployment mode from API
   * @returns {Promise}
   */
  getDeploymentMode() {
    return fetch(`${ROOT_URL}deploymentMode`).get();
  },

  /**
   * Get Report Configuration
   * @param {String} settings
   * @return {Promise}
   */
  getReportConfiguration() {
    return fetch(`${ROOT_URL}reportSetting`).get();
  },

  /**
   * Save Report Configuration
   * @param  {Object} data
   */
  saveReportConfiguration(data: any) {
    data = data || {};
    return fetch(`${ROOT_URL}reportSetting`).post(data);
  },

  /**
   * Get Help System URL
   */
  getHelpSystemUrl() {
    return fetch(`${ROOT_URL}helpSystemUrl`).get();
  },

    /**
   * Get Notification Interval
   */
  getNotificationInterval() {
    return fetch(`${ROOT_URL}notificationInterval`).get();
  },

  /**
   * Load category settings.
   */
  categorySetting() {
    return fetch(`${ROOT_URL}categorySetting`).get();
  },

  /**
   * Fetch pre-defined themes
   */
  fetchThemes: CacheUtils.memoize(() => fetch(`${ROOT_URL}themes`).get()),

  /**
   * Fetch caching configuration
   */
  loadCachingConfiguration() {
    return fetch(`${ROOT_URL}cacheConfiguration`).get();
  },
  /**
   * save caching configuration
   */
  saveCachingConfiguration(data: any) {
    return fetch(`${ROOT_URL}cacheConfiguration`).post(data);
  },

  /**
   * get google map configuration
   */
  loadGoogleMapConfiguration(tenantId: string) {
    tenantId = tenantId || '';
    return fetch(`${ROOT_URL}googleAPIKey/${tenantId}`).get();
  },
  /**
   * save google map configuration
   */
  saveGoogleMapConfiguration(data: any) {
    return fetch(`${ROOT_URL}googleAPIKey`).post(data);
  },

  /**
   * get exporting configuration
   */
  loadExportingConfiguration() {
    return fetch(`${ROOT_URL}exportQueueSetting`).get();
  },
  /**
   * save exporting configuration
   */
  saveExportingConfiguration(data: any) {
    return fetch(`${ROOT_URL}exportQueueSetting`).post(data);
  }
};



// WEBPACK FOOTER //
// ./ui/fetchers/Izenda.SystemSettingFetcher.js